import React, { useState, useRef, useEffect } from "react";
import PopoutUtilityToolbarGrid from "../utilities/PopoutUtilityToolbarGrid";

// `onToggleSelect` is a callback invoked when a toggle-type button is toggled
// it will be passed either the button object or `null` when deselected.
const R6UtilityToolbar = ({ onTextToggleSelect, onBackgroundToggleSelect, selectedMap, onCamsToggle }) => {
  const [selectedButton, setSelectedButton] = useState("background");
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [popoutAnchor, setPopoutAnchor] = useState({ x: 0, y: 0 });
  const [selectedCategory, setSelectedCategory] = useState("attacker");
  // toggle options popup state
  const [textToggleOptionsOpen, setTextToggleOptionsOpen] = useState(false);
  const [textToggleOptionsAnchor, setTextToggleOptionsAnchor] = useState({ x: 0, y: 0 });
  const [activeTextToggleButton, setActiveTextToggleButton] = useState(null); // the button whose options are open
  const [selectedTextToggleSelection, setSelectedTextToggleSelection] = useState(null); 
  const [siteOptions, setSiteOptions] = useState([]);
  const textToggleOptionsRef = useRef(null);

  // compute paths based on selectedMap object if available
  const mapFolder = selectedMap ? (selectedMap.basemap || (selectedMap.id ? (selectedMap.id.charAt(0).toUpperCase() + selectedMap.id.slice(1)) : null)) : null;
  const backgroundMapSrc = selectedMap && mapFolder ? `/maps/R6S/${mapFolder}/${selectedMap.id}.png` : null;

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug && console.debug('R6UtilityToolbar selectedMap:', selectedMap);
    // If the selectedMap already contains `sites`, use them directly.
    if (selectedMap && Array.isArray(selectedMap.sites)) {
      // eslint-disable-next-line no-console
      console.debug && console.debug('R6UtilityToolbar using inline sites:', selectedMap.sites);
      setSiteOptions(selectedMap.sites);
      return;
    }

    // Otherwise attempt to dynamically import the JSON by basemap, id, or name.
    const mapKey = selectedMap && (selectedMap.basemap || selectedMap.id || selectedMap);
    if (!mapKey || typeof mapKey !== 'string') {
      setSiteOptions([]);
      return;
    }

    const tryImport = async (name) => {
      try {
        const module = await import(`../data/maps/r6s/${name}.json`);
        const data = module && (module.default || module);
        return (data && data.sites) || [];
      } catch (err) {
        return null;
      }
    };

    (async () => {
      // try as-provided first
      let sites = await tryImport(mapKey);
      if (sites === null) {
        // try capitalized (e.g. 'villa' -> 'Villa')
        const cap = mapKey.charAt(0).toUpperCase() + mapKey.slice(1);
        sites = await tryImport(cap);
      }
      if (sites === null) sites = [];
      // eslint-disable-next-line no-console
      console.debug && console.debug('R6UtilityToolbar loaded sites for', mapKey, sites);
      setSiteOptions(sites);
    })();
  }, [selectedMap]);

  // buttonType: 'images' will open the popout grid 
  // buttonType: 'toggle' will simply toggle a text-mode selection and
  // invoke `onToggleSelect` with the associated object.
  // in the latter case `category` is unused but we can carry an
  // `imageSrc` property that the whiteboard can render when active.
  const buttons = [
    { id: "attackers", label: "Attackers", category: "attacker", buttonType: "images" },
    { id: "defenders", label: "Defenders", category: "defender", buttonType: "images" },
    { id: "utility", label: "Utility", category: "utility", buttonType: "images" },
    { id: "sites", label: "Sites", buttonType: "textToggle", textToggleOptions: siteOptions },
    { id: "cams", label: "Cams", buttonType: "toggle" },
    { id: "background", label: "Background", category: "background", buttonType: "toggle", src: backgroundMapSrc },
  ];

  const handleButtonClick = (e, button) => {
    const rect = e.currentTarget.getBoundingClientRect();

    // for toggle buttons: simply toggle the selection without opening a menu
    if (button.buttonType === "toggle") {
      const isActive = selectedButton === button.id;
      if (button.id === "cams") {
        const nextValue = !isActive;
        setSelectedButton(nextValue ? button.id : null);
        onCamsToggle && onCamsToggle(nextValue);
        return;
      }

      if (isActive) {
        // deselect
        setSelectedButton(null);
        // Call the background toggle callback
        onBackgroundToggleSelect && onBackgroundToggleSelect(null);
      } else {
        // select
        setSelectedButton(button.id);
        onBackgroundToggleSelect && onBackgroundToggleSelect({ id: button.id, label: button.label, imageSrc: button.src });
      }
      return;
    }

    // for text buttons: open a popover of text-options (one step before selecting)
    if (button.buttonType === "textToggle") {
      // estimate popover height: padding + options
      const estimatedHeight = 16 + (button.textToggleOptions?.length || 0) * 28;
      const GAP = 12; // extra gap so popover doesn't overlap the toolbar
      setActiveTextToggleButton(button);
      setTextToggleOptionsAnchor({ x: rect.left, y: rect.top - estimatedHeight - GAP });
      setTextToggleOptionsOpen((v) => !v);
      // close image popout if open
      setPopoutOpen(false);
      return;
    }

    // fallback / default is images
    onTextToggleSelect && onTextToggleSelect(null);
    setSelectedButton(button.id);
    setSelectedCategory(button.category);
    setPopoutAnchor({ x: rect.left, y: rect.top });
    setPopoutOpen(!popoutOpen);
  };

  const handleSelectUtility = (src) => {
    // Handle the selected utility icon here
  };

  // handle selection/deselection of a text-option
  const handleTextToggleOptionClick = (opt) => {
    if (!activeTextToggleButton) return;
    const key = `${activeTextToggleButton.id}:${opt.id}`;
    if (selectedTextToggleSelection === key) {
      // deselect
      setSelectedTextToggleSelection(null);
      onTextToggleSelect && onTextToggleSelect(null);
    } else {
      setSelectedTextToggleSelection(key);
      onTextToggleSelect && onTextToggleSelect({ id: key, label: opt.label, imageSrc: opt.imageSrc });
      // mark parent button as active
      setSelectedButton(activeTextToggleButton.id);
    }
    setTextToggleOptionsOpen(false);
    setActiveTextToggleButton(null);
  };

  // close text options if clicking outside
  useEffect(() => {
    if (!textToggleOptionsOpen) return;
    const handler = (e) => {
      if (textToggleOptionsRef.current && !textToggleOptionsRef.current.contains(e.target)) {
        setTextToggleOptionsOpen(false);
        setActiveTextToggleButton(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [textToggleOptionsOpen]);

  return (
    <>
      <div className="utility-toolbar" role="toolbar" aria-orientation="horizontal">
        {buttons.map((b) => {
          const isActiveTextToggle = selectedTextToggleSelection && selectedTextToggleSelection.startsWith(b.id + ":");
          const isActive = selectedButton === b.id || isActiveTextToggle;
          return (
            <button
              key={b.id}
              className={`utility-button ${isActive ? "active" : ""}`}
              title={b.label}
              aria-label={b.label}
              onClick={(e) => handleButtonClick(e, b)}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      <PopoutUtilityToolbarGrid
        visible={popoutOpen}
        anchor={popoutAnchor}
        category={selectedCategory}
        onSelect={handleSelectUtility}
        onClose={() => setPopoutOpen(false)}
      />

      {/* Text toggle options popover (floating div) */}
      {textToggleOptionsOpen && activeTextToggleButton && (
        <div
          ref={textToggleOptionsRef}
          role="dialog"
          aria-hidden={!textToggleOptionsOpen}
          style={{
            position: "fixed",
            left: textToggleOptionsAnchor.x,
            top: textToggleOptionsAnchor.y,
            backgroundColor: "#222",
            border: "1px solid #444",
            borderRadius: 4,
            padding: 8,
            zIndex: 2000,
          }}
        >
          {activeTextToggleButton.textToggleOptions && activeTextToggleButton.textToggleOptions.map((opt) => (
            <div
              key={opt.id}
              onClick={() => handleTextToggleOptionClick(opt)}
              style={{ padding: "6px 10px", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default R6UtilityToolbar;